import { Form } from 'react-bootstrap';
import { I18n } from '@/i18n/I18n';
import { ParameterSubjectType, ParameterType, ParameterInputType } from '@/types/parameterTypes';
import { Operation } from '@/backend/models/operation';
import { Parameter } from '@/backend/models/parameter';
import { buildSelectableParameters, useCreateSourceReference, useGetAllRefsAndParametersByConsumer, useGetParametersToAssignFromOutput, useParameterHasCorrectInputType, useParameterHasCorrectSubject } from '@/frontend/hooks/parameters';
import { OutputParameterSelect } from './OutputParameterSelect';
import { InputParameterSelect } from './InputParameterSelect';
import { AssignValueFromSelect } from './AssignValueFromSelect';
import { useParameterStore } from '@/frontend/store/ParameterStore';
import { useTreeStore } from '@/frontend/store/TreeStore';
import { MultiParameterSelect } from './MultiParameterSelect';

export function ParameterLink(props: { parameter: Parameter, operation?: Operation }) {
  const { parameter, operation } = props;

  if (parameter.subjectType === ParameterSubjectType.STEP) {
    if (parameter.type === ParameterType.INPUT) {
      return <StepParameterSelect parameter={parameter} />;
    }
    return null;
  }

  if (parameter.subjectType === ParameterSubjectType.VALIDATION) {
    if (parameter.type === ParameterType.INPUT) {
      return <ValidationParameterSelect parameter={parameter} />;
    }
    return null;
  }

  if(parameter.subjectType === ParameterSubjectType.PROCEDURE && parameter.type === ParameterType.INPUT) {
    return <ProcedureParameterSelect parameter={parameter} />;
  }

  if (operation) {
    if (parameter.type === ParameterType.INPUT) {
      return <div>
        <Form.Label className="col-form-label col-form-label-sm">{I18n.get('assignValueFrom')}:</Form.Label>
        <InputParameterSelect operation={operation} parameter={parameter} />
      </div>;
    }

    if (parameter.type === ParameterType.FIELD) {
      return (
        <>
          <tr>
            <td className='pe-2'>{I18n.get('assignValueFrom')}:</td>
            <td><InputParameterSelect operation={operation} parameter={parameter} /></td>
          </tr>
          { parameter.inputType !== ParameterInputType.TABLE && <tr>
            <td>{I18n.get('assignValueTo')}:</td>
            <td><OutputParameterSelect operation={operation} parameter={parameter} /></td>
          </tr> }
        </>
      );
    }

    return <div>
      <Form.Label className="col-form-label col-form-label-sm">{I18n.get('assignValueTo')}:</Form.Label>
      <OutputParameterSelect operation={operation} parameter={parameter} />
    </div>;
  }
}

export function StepParameterSelect(props: { parameter: Parameter }) {
  const { parameter } = props;
  const selectableParameters = useGetParametersToAssignFromOutput(parameter.subjectId);

  return (
    <AssignValueFromSelect
      parameter={parameter}
      selectableParameters={selectableParameters}
    />
  );
}

export function ProcedureParameterSelect(props: { parameter: Parameter }) {
  const { parameter } = props;
  const selectableParameters = useParameterStore(state => state.parameters).filter(param => 
    param.type === ParameterType.CONFIG 
    && param.subjectType === ParameterSubjectType.PROCEDURE
  );

  return (
    <AssignValueFromSelect
      parameter={parameter}
      selectableParameters={selectableParameters}
    />
  );
}

function ValidationParameterSelect(props: { parameter: Parameter }) {
  const { parameter } = props;
  const operations = useTreeStore(state => state.operations);
  const currentSelections = useGetAllRefsAndParametersByConsumer(parameter.id);
  const currentSelectionSubjectIds = currentSelections.map(selection => selection.currentParameter?.subjectId);
  const currentSelectionScenarioIds = operations.filter(op => currentSelectionSubjectIds.includes(op.id)).map(op => op.parentId);

  const validation = useParameterStore(state => state.validations.find(v => v.id === parameter.subjectId));
  const parameterHasCorrectSubject = useParameterHasCorrectSubject(parameter, validation?.subjectId || '');
  const parameters = useParameterStore(state => state.parameters).filter(p => {
    if (p.subjectType === ParameterSubjectType.OPERATION) {
      const scenarioId = operations.find(op => op.id === p.subjectId)?.parentId;
      if (scenarioId && currentSelectionScenarioIds.includes(scenarioId)) {
        return false;
      }
    }
    return parameterHasCorrectSubject(p);
});

  const parameterHasCorrectInputType = useParameterHasCorrectInputType(parameter.inputType);
  const selectableParameters = buildSelectableParameters(parameters, operations)
  .filter(param => param.fieldType === parameter.fieldType && parameterHasCorrectInputType(param));

  const createReference = useCreateSourceReference(parameter.id);

  return (
    <div className='flex-shrink' style={{ maxWidth: '50%', minWidth: '150px' }}>
      <Form.Label className="col-form-label col-form-label-sm">{I18n.get('assignValueFrom')}:</Form.Label>
      <MultiParameterSelect
        parameters={selectableParameters}
        currentSelections={currentSelections}
        createReference={createReference}
      />
    </div>
  );
}
